import { useRef, useEffect, useState, DependencyList } from 'react';
import * as d3 from 'd3';
import logger from '../utils/logger';
import { useD3Zoom, ZoomOptions } from './useD3Zoom';

/**
 * D3チャート初期化コンテキスト
 */
export interface D3ChartSetupContext {
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  width: number;
  height: number;
  innerWidth: number;
  innerHeight: number;
  margin: { top: number; right: number; bottom: number; left: number };
}

/**
 * D3チャート描画コンテキスト
 */
export interface D3ChartRenderContext<TRefs> {
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  refs: TRefs;
  width: number;
  height: number;
  innerWidth: number;
  innerHeight: number;
  margin: { top: number; right: number; bottom: number; left: number };
  currentTransform?: d3.ZoomTransform;  // enableZoom=trueの場合のみ
}

/**
 * useD3Chart - D3可視化の初期化と描画を管理するフック
 *
 * 利点:
 * - useEffectを明示的に書かなくて済む
 * - 初期化と描画ロジックを分離
 * - ResizeObserver対応
 * - D3要素の参照を自動管理
 *
 * @param config - チャート設定
 * @returns containerRef - コンテナ要素のref
 */
export function useD3Chart<TRefs = Record<string, unknown>>(config: {
  /** デフォルト幅 */
  defaultWidth: number;
  /** デフォルト高さ */
  defaultHeight: number;
  /** ResizeObserverでwidthを監視するか（デフォルト: true） */
  monitorWidth?: boolean;
  /** ResizeObserverでheightを監視するか（デフォルト: true） */
  monitorHeight?: boolean;
  /** ズーム・パン機能を有効にするか（デフォルト: false） */
  enableZoom?: boolean;
  /** ズーム機能のオプション（enableZoom=trueの場合のみ有効） */
  zoomOptions?: ZoomOptions;
  /** マージン設定 */
  margin: { top: number; right: number; bottom: number; left: number };
  /** 初期化関数（一度だけ実行） */
  setup: (context: D3ChartSetupContext) => TRefs;
  /** 描画関数（依存配列が変更されたら実行） */
  render: (context: D3ChartRenderContext<TRefs>) => void;
  /** 描画の依存配列 */
  dependencies: DependencyList;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [actualWidth, setActualWidth] = useState<number | null>(null);
  const [actualHeight, setActualHeight] = useState<number | null>(null);

  // D3要素の参照を保持
  const d3Refs = useRef<{
    svg?: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    g?: d3.Selection<SVGGElement, unknown, null, undefined>;
    userRefs?: TRefs;
  }>({});

  const shouldMonitorWidth = config.monitorWidth ?? true;
  const shouldMonitorHeight = config.monitorHeight ?? true;

  // ResizeObserver（debounce付き）
  useEffect(() => {
    if (!containerRef.current) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let hasValidated = false;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;

        // 初回のみ：循環依存の検証
        if (!hasValidated) {
          const container = entry.target as HTMLElement;

          // height監視の循環依存チェック
          if (shouldMonitorHeight) {
            // containerまたはその祖先に明示的な高さ制約があるかチェック
            let hasHeightConstraint = false;
            let current: HTMLElement | null = container;

            // 最大10階層まで祖先をチェック
            for (let i = 0; i < 10 && current; i++) {
              const style = current.style;
              const computed = getComputedStyle(current);

              // 具体的な高さ指定がある
              if (style.height && style.height !== 'auto' && style.height !== '') {
                hasHeightConstraint = true;
                break;
              }

              // computedStyleに具体的な値がある場合
              if (computed.height !== 'auto' && computed.height !== '0px' && computed.height !== '') {
                const computedHeightValue = parseInt(computed.height);
                if (computedHeightValue > 0) {
                  hasHeightConstraint = true;
                  break;
                }
              }

              current = current.parentElement;
            }

            if (!hasHeightConstraint) {
              logger.assert(
                false,
                '[useD3Chart] Circular dependency detected: monitorHeight=true, ' +
                'but neither container nor its ancestors have explicit height constraint. ' +
                'Container will grow based on SVG size, while SVG size depends on container via ResizeObserver. ' +
                'This creates infinite loop. ' +
                'Solution: Either (1) Set monitorHeight=false, or (2) Add explicit height (e.g., style={{ height: 200 }})'
              );
            }
          }

          // width監視の循環依存チェック
          if (shouldMonitorWidth) {
            // containerまたはその祖先に明示的な幅制約があるかチェック
            let hasWidthConstraint = false;
            let current: HTMLElement | null = container;

            // 最大10階層まで祖先をチェック
            for (let i = 0; i < 10 && current; i++) {
              const style = current.style;
              const computed = getComputedStyle(current);

              // 具体的な幅指定がある
              if (style.width && style.width !== 'auto' && style.width !== '') {
                hasWidthConstraint = true;
                break;
              }

              // computedStyleに具体的な値がある場合
              if (computed.width !== 'auto' && computed.width !== '0px' && computed.width !== '') {
                const computedWidthValue = parseInt(computed.width);
                if (computedWidthValue > 0) {
                  hasWidthConstraint = true;
                  break;
                }
              }

              current = current.parentElement;
            }

            if (!hasWidthConstraint) {
              logger.assert(
                false,
                '[useD3Chart] Circular dependency detected: monitorWidth=true, ' +
                'but neither container nor its ancestors have explicit width constraint. ' +
                'Container will grow based on SVG size, while SVG size depends on container via ResizeObserver. ' +
                'This creates infinite loop. ' +
                'Solution: Either (1) Set monitorWidth=false, or (2) Add explicit width (e.g., style={{ width: 400 }})'
              );
            }
          }

          hasValidated = true;
        }

        // 既存のタイマーをキャンセル
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }

        // debounce: 100ms後にサイズを更新
        timeoutId = setTimeout(() => {
          if (shouldMonitorWidth) {
            setActualWidth(width);
          }
          if (shouldMonitorHeight) {
            setActualHeight(height);
          }
          timeoutId = null;
        }, 100);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      resizeObserver.disconnect();
    };
  }, [shouldMonitorWidth, shouldMonitorHeight, config.defaultWidth, config.defaultHeight]);

  const width = shouldMonitorWidth ? (actualWidth ?? config.defaultWidth) : config.defaultWidth;
  const height = shouldMonitorHeight ? (actualHeight ?? config.defaultHeight) : config.defaultHeight;
  // 負の値を防ぐ（コンテナサイズがmarginより小さい場合）
  const innerWidth = Math.max(0, width - config.margin.left - config.margin.right);
  const innerHeight = Math.max(0, height - config.margin.top - config.margin.bottom);

  // 初期化（一度だけ実行）
  useEffect(() => {
    if (!svgRef.current) return;
    if (d3Refs.current.svg) return;

    const svg = d3.select(svgRef.current);
    d3Refs.current.svg = svg;

    svg.attr('width', width).attr('height', height);

    // グループ要素を作成
    const g = svg.append('g')
      .attr('transform', `translate(${config.margin.left},${config.margin.top})`);
    d3Refs.current.g = g;

    // ユーザー定義の初期化を実行
    const userRefs = config.setup({
      svg,
      g,
      width,
      height,
      innerWidth,
      innerHeight,
      margin: config.margin
    });

    d3Refs.current.userRefs = userRefs;
    // 初期化は一度だけ実行
    // config, width, heightはsetup時に使用されるが、リサイズ時はrenderで更新されるため依存配列には含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // サイズ更新（width/heightが変わったらSVGサイズとグループのtransformを更新）
  useEffect(() => {
    const { svg, g } = d3Refs.current;
    if (!svg || !g) return;

    svg.attr('width', width).attr('height', height);
    g.attr('transform', `translate(${config.margin.left},${config.margin.top})`);
  }, [width, height, config.margin]);

  // ズーム機能（オプショナル）
  const [currentTransform, setCurrentTransform] = useState<d3.ZoomTransform>(d3.zoomIdentity);

  const enableZoom = config.enableZoom ?? false;
  useD3Zoom(
    enableZoom ? svgRef : { current: null },
    enableZoom ? setCurrentTransform : undefined,
    config.zoomOptions || {}
  );

  // 描画（依存配列が変更されたら実行）
  useEffect(() => {
    const { svg, g, userRefs } = d3Refs.current;
    if (!svg || !g || !userRefs) return;
    // サイズが0の場合は描画をスキップ（レイアウト計算中）
    if (innerWidth <= 0 || innerHeight <= 0) return;

    config.render({
      svg,
      g,
      refs: userRefs,
      width,
      height,
      innerWidth,
      innerHeight,
      margin: config.margin,
      currentTransform: enableZoom ? currentTransform : undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height, innerWidth, innerHeight, config.margin, currentTransform, ...config.dependencies]);

  return {
    containerRef,
    svgRef,
    width,
    height,
    currentTransform: enableZoom ? currentTransform : undefined
  };
}
